import React from 'react';
import PropTypes from 'prop-types';
import { FieldArray, withFormik } from 'formik';
import { get, map, uniqueId } from 'lodash';
import Yup from 'yup';
import {
  AddButton,
  Button,
  ButtonWrapper,
  Form,
  FormButtons,
  FormSectionHeader,
  ErrorMessage,
} from '../../common';
import { getFormValue } from '../../../util/helpers';
import CaregiverForm, { caregiverValidationSchema } from './CaregiverForm';

const DEFAULT_CAREGIVER = {
  addressId: undefined,
  addressState: '',
  city: '',
  email: '',
  firstName: '',
  id: undefined,
  lastName: '',
  primaryPhoneNumber: '',
  relationship: '',
  secondaryStreetAddress: '',
  streetAddress: '',
  zipCode: '',
  notes: '',
};

const EMERGENCY_CONTACTS = 'emergencyContacts';

const getNewCaregiver = () => ({
  ...DEFAULT_CAREGIVER,
  tempId: uniqueId('temp-'),
});

const renderEmergencyContacts = ({ fieldArray, props }) => {
  const { setFieldValue, values } = props;
  return (
    <React.Fragment>
      {map(values[EMERGENCY_CONTACTS], (contact, index) => (
        <div key={contact.id || contact.tempId}>
          <FormSectionHeader
            onRemove={() => fieldArray.remove(index)}
            title={`Emergency Contact ${index + 1}`}
          />
          <CaregiverForm
            paths={[fieldArray.name, index]}
            setFieldValue={setFieldValue}
            values={values}
          />
        </div>
      ))}
      <AddButton onClick={() => fieldArray.push(getNewCaregiver())}>
        Add Emergency Contact
      </AddButton>
    </React.Fragment>
  );
};

renderEmergencyContacts.propTypes = {
  fieldArray: PropTypes.array.isRequired,
  props: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
};

class PrimaryCaregiverForm extends React.Component {
  handleCancel = () => {
    this.props.resetForm();
    this.props.onCancel();
  };

  render() {
    const {
      handleSubmit,
      isEditing,
      setFieldValue,
      status,
      values,
    } = this.props;
    const submitButtonText = isEditing ? 'Save' : 'Save & Continue';

    return (
      <Form onSubmit={handleSubmit}>
        <CaregiverForm setFieldValue={setFieldValue} values={values} />
        <FieldArray
          name={EMERGENCY_CONTACTS}
          render={fieldArray =>
            renderEmergencyContacts({ fieldArray, props: this.props })
          }
        />
        {get(status, 'saveError') && (
          <ErrorMessage>{status.saveError}</ErrorMessage>
        )}
        <FormButtons>
          <ButtonWrapper>
            <Button type="submit">{submitButtonText}</Button>
          </ButtonWrapper>
          {isEditing && (
            <ButtonWrapper marginLeft>
              <Button
                color="default"
                onClick={this.handleCancel}
                variant="text"
              >
                Cancel
              </Button>
            </ButtonWrapper>
          )}
        </FormButtons>
      </Form>
    );
  }
}

const caregiverPropTypes = {
  addressId: PropTypes.number,
  addressState: PropTypes.string,
  city: PropTypes.string,
  email: PropTypes.string,
  firstName: PropTypes.string,
  id: PropTypes.number,
  lastName: PropTypes.string,
  notes: PropTypes.string,
  primaryPhoneNumber: PropTypes.string,
  relationship: PropTypes.string,
  secondaryStreetAddress: PropTypes.string,
  streetAddress: PropTypes.string,
  zipCode: PropTypes.string,
};

PrimaryCaregiverForm.propTypes = {
  ...caregiverPropTypes,
  handleSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  resetForm: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  emergencyContacts: PropTypes.arrayOf(PropTypes.shape(caregiverPropTypes)),
  isEditing: PropTypes.bool,
  status: PropTypes.object,
};

PrimaryCaregiverForm.defaultProps = {
  ...DEFAULT_CAREGIVER,
  emergencyContacts: [],
  isEditing: false,
};

const getCaregiverValues = props => ({
  id: props.id,
  tempId: props.tempId,
  addressState: getFormValue(props, 'addressState', ''),
  city: getFormValue(props, 'city', ''),
  email: getFormValue(props, 'email', ''),
  firstName: getFormValue(props, 'firstName', ''),
  lastName: getFormValue(props, 'lastName', ''),
  notes: getFormValue(props, 'notes', ''),
  primaryPhoneNumber: getFormValue(props, 'primaryPhoneNumber', ''),
  relationship: getFormValue(props, 'relationship', ''),
  secondaryStreetAddress: getFormValue(props, 'secondaryStreetAddress', ''),
  streetAddress: getFormValue(props, 'streetAddress', ''),
  zipCode: getFormValue(props, 'zipCode', ''),
});

export default withFormik({
  displayName: 'PrimaryCaregiver',
  handleSubmit: (values, { props, setStatus }) => {
    return props
      .onSave({
        ...values,
        id: props.id,
        addressId: props.addressId,
      })
      .then(() => (props.isEditing ? props.onCancel() : props.onContinue()))
      .catch(error => setStatus({ saveError: error }));
  },
  mapPropsToValues: ({ emergencyContacts, ...props }) => ({
    ...getCaregiverValues(props),
    emergencyContacts: map(emergencyContacts, getCaregiverValues),
  }),
  validationSchema: Yup.object().shape({
    ...caregiverValidationSchema,
    [EMERGENCY_CONTACTS]: Yup.array().of(
      Yup.object().shape(caregiverValidationSchema),
    ),
  }),
})(PrimaryCaregiverForm);



// WEBPACK FOOTER //
// ./src/modules/PrimaryCaregiverPage/components/PrimaryCaregiverForm.js